import type { FocusEvent } from 'react';
import { Component } from 'react';
import type { MenuPosition, SingleValueProps } from 'react-select';
import { components } from 'react-select';
import { EnvironmentMarkerWithName } from '@gonfalon/environments/EnvironmentMarkerWithName';
import type { List } from '@gonfalon/immutable-shim/immutableShim';
import type { OptionProps, StylesObject } from '@gonfalon/launchpad-experimental/CustomSelect';
import { CustomSelect } from '@gonfalon/launchpad-experimental/CustomSelect';
import cx from 'clsx';

import type { Environment } from '#utils/environments/records/Environment';
import { toMarkerEnv } from '#utils/environments/toMarkerEnv';
import type { ProjectWithEnvironmentEntities } from '#utils/projects/records/ProjectWithEnvironmentEntities';
import { makeFilter } from '#utils/stringUtils';

import type { EnvironmentSelectOption } from './EnvironmentSelect';

export type EnvironmentMultiSelectProps = {
  selectedEnvironments?: Environment[];
  placeholder?: string;
  environments?: List<Environment>;
  projects?: List<ProjectWithEnvironmentEntities>;
  isDisabled?: boolean;
  onChange: (environmentIds: string[], options?: EnvironmentSelectOption[]) => void;
  onInputChange?: (value: string) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  className?: string;
  id?: string;
  name?: string;
  autoFocus?: boolean;
  isLoading?: boolean;
  styles?: StylesObject;
  menuPosition?: MenuPosition;
  isMulti?: boolean;
};

type ProjectHeading = {
  label: string;
  isHeading: true;
  disabled: true;
  name: string;
  key: string;
  projectName: string;
  projectKey: string;
};

export class EnvironmentMultiSelect extends Component<EnvironmentMultiSelectProps> {
  render() {
    const { selectedEnvironments, className, styles, placeholder, isMulti = true, ...props } = this.props;
    const options = this.getOptions();

    const SingleValue = (singleValueProps: SingleValueProps<EnvironmentSelectOption>) => (
      <components.SingleValue {...singleValueProps}>
        {this.renderEnvironmentOption(singleValueProps.data)}
      </components.SingleValue>
    );

    const selectedValues = () => {
      if (selectedEnvironments && selectedEnvironments.length !== 0) {
        return options.filter(
          (option) => 'value' in option && selectedEnvironments.some((env) => env._id === option.value),
        );
      }

      return [];
    };

    return (
      <CustomSelect
        ariaLabel="Select an environment"
        className="EnvironmentSelect"
        placeholder={placeholder ?? 'Select an environment'}
        {...props}
        value={selectedValues()}
        isOptionDisabled={(option: OptionProps) => !!option.disabled}
        options={options}
        onChange={this.handleChange}
        // @ts-expect-error this prop should be refactored to use "formatOptionLabel", with "getOptionLabel" returning a string version of the label
        getOptionLabel={this.renderOption}
        filterOption={this.filterOptions}
        customComponents={{ SingleValue }}
        onInputChange={this.handleInputChange}
        isLoading={this.props.isLoading}
        styles={styles}
        menuPosition={this.props.menuPosition}
        isMulti={isMulti}
      />
    );
  }

  getOptions = () => {
    const { environments, projects } = this.props;
    const createEnvironmentOptions = (envs: List<Environment>): EnvironmentSelectOption[] =>
      envs
        .map((environment) => ({
          value: environment._id,
          label: environment.name,
          environment,
          key: environment.key,
        }))
        .toArray();
    if (environments && !environments.isEmpty()) {
      return createEnvironmentOptions(environments);
    }
    const createEnvironmentOptionsFromProject = (
      project: ProjectWithEnvironmentEntities,
    ): Array<EnvironmentSelectOption | ProjectHeading> =>
      project.environments
        .map((environment) => ({
          value: environment._id,
          label: environment.name,
          environment,
          key: environment.key,
          projectName: project.name,
          projectKey: project.key,
        }))
        .toArray();
    if (projects) {
      return projects.reduce<Array<EnvironmentSelectOption | ProjectHeading>>(
        (options, project) => [
          ...options,
          {
            label: project.name,
            isHeading: true,
            disabled: true,
            // create filterable
            name: project.environments
              .map((e) => e.name)
              .toJS()
              .join(','),
            key: project.environments
              .map((e) => e.key)
              .toJS()
              .join(','),
            projectName: project.name,
            projectKey: project.key,
          },
          ...createEnvironmentOptionsFromProject(project),
        ],
        [],
      );
    }

    return [];
  };

  renderProjectHeading = (option: ProjectHeading) => (
    <span className="u-subtle u-fw-semibold u-fs-sm">{option.label}</span>
  );

  renderEnvironmentOption = (option: EnvironmentSelectOption, shouldNestEnvironments?: boolean) => {
    const { environment } = option;
    return (
      <span className={cx('u-flex', 'u-flex-row', 'u-flex-middle', 'u-va-tb', { 'u-ml-l': shouldNestEnvironments })}>
        <EnvironmentMarkerWithName env={toMarkerEnv(environment)} />
      </span>
    );
  };

  renderOption = (option: EnvironmentSelectOption | ProjectHeading) => {
    const shouldNestEnvironments = !!this.props.projects;
    if ('isHeading' in option) {
      return option.isHeading && this.renderProjectHeading(option);
    }
    return this.renderEnvironmentOption(option, shouldNestEnvironments);
  };

  handleChange = (option: EnvironmentSelectOption[] | EnvironmentSelectOption | null) => {
    const { isMulti = true } = this.props;
    if (isMulti) {
      const multiOption = option as EnvironmentSelectOption[] | null;
      multiOption
        ? this.props.onChange(
            multiOption.map((o) => o.value),
            multiOption,
          )
        : this.props.onChange([], []);
    } else {
      const singleOption = option as EnvironmentSelectOption | null;
      this.props.onChange(singleOption ? [singleOption.value] : [], singleOption ? [singleOption] : []);
    }
  };

  handleInputChange = (value: string) => {
    this.props.onInputChange?.(value);
  };

  filterOptions = (option: OptionProps, input: string) => {
    const filter = makeFilter(input, 'name', 'key', 'projectName', 'projectKey');
    return filter(option.data);
  };
}
